if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        if (!this.elements.thumbnails) return;

        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.elements.viewer.hasAttribute('data-mobile-thumbnail-mode')) {
          this.setupMobileThumbnailMode();
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.pageCount = this.sliderItems.length;
        this.currentPage = 1;
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.isAnimating = false;
        this.setupEventListeners();
      }

      setupMobileThumbnailMode() {
        // This ensures the first image is always visible on mobile when in thumbnail mode
        if (window.matchMedia('(max-width: 749px)').matches) {
          const firstMediaItem = this.elements.viewer.querySelector('.product__media-item:first-child');
          if (firstMediaItem) {
            firstMediaItem.classList.add('is-active');

            // Hide all other media items
            const otherMediaItems = this.elements.viewer.querySelectorAll('.product__media-item:not(:first-child)');
            otherMediaItems.forEach((item) => {
              item.style.display = 'none';
            });
          }
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        // Pause all media first before showing new one
        this.pauseAllActiveMedia();

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
          // Make sure videos are hidden when not active
          if (element.dataset.mediaType === 'video' || element.dataset.mediaType === 'external_video') {
            element.style.display = 'none';
          }
        });

        activeMedia?.classList?.add('is-active');
        // Make sure active media is visible
        activeMedia.style.display = '';

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });

        this.playActiveMedia(activeMedia);
        this.announceLiveRegion(activeMedia, activeMedia.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      pauseAllActiveMedia() {
        // Pause all videos in the viewer
        this.elements.viewer.querySelectorAll('video').forEach((video) => {
          video.pause();
        });

        // Pause YouTube videos
        this.elements.viewer.querySelectorAll('.js-youtube').forEach((iframe) => {
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
        });

        // Pause Vimeo videos
        this.elements.viewer.querySelectorAll('.js-vimeo').forEach((iframe) => {
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          }
        });
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;

        // Initialize deferred video immediately if present
        const deferredMedia = activeItem.querySelector('deferred-media');
        if (deferredMedia && !deferredMedia.getAttribute('loaded')) {
          // Force autoplay for video media
          if (!deferredMedia.hasAttribute('data-autoplay')) {
            deferredMedia.setAttribute('data-autoplay', 'true');
          }
          deferredMedia.loadContent(false); // Don't focus so it doesn't interrupt browsing
        } else if (deferredMedia && deferredMedia.getAttribute('loaded')) {
          // If already loaded, ensure video plays
          const video = deferredMedia.querySelector('video');
          if (video) {
            video.play().catch((e) => console.warn('Video play error:', e));
          }

          // If it's a YouTube or Vimeo video that's already loaded
          const iframe = deferredMedia.querySelector('iframe');
          if (iframe) {
            if (iframe.classList.contains('js-youtube')) {
              iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            } else if (iframe.classList.contains('js-vimeo')) {
              iframe.contentWindow.postMessage('{"method":"play"}', '*');
            }
          }
        }

        // Handle model viewer
        const modelViewer = activeItem.querySelector('model-viewer');
        if (modelViewer) {
          modelViewer.play();
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      setupEventListeners() {
        // Existing event listeners
        const prevButton = this.querySelector('.slider-button--prev');
        const nextButton = this.querySelector('.slider-button--next');

        if (prevButton) {
          prevButton.addEventListener('click', () => this.onButtonClick('prev'));
        }
        if (nextButton) {
          nextButton.addEventListener('click', () => this.onButtonClick('next'));
        }

        // Add touch event listeners for mobile swipe
        if (window.innerWidth <= 750) {
          let touchStartY = 0;
          let isScrolling = false;

          this.slider.addEventListener(
            'touchstart',
            (e) => {
              this.touchStartX = e.changedTouches[0].screenX;
              touchStartY = e.changedTouches[0].screenY;
              this.isAnimating = false;
              isScrolling = false;
            },
            { passive: true }
          );

          this.slider.addEventListener(
            'touchmove',
            (e) => {
              if (this.isAnimating) return;

              const currentX = e.changedTouches[0].screenX;
              const currentY = e.changedTouches[0].screenY;
              const diffX = currentX - this.touchStartX;
              const diffY = currentY - touchStartY;

              // Determine if the user is trying to scroll vertically
              if (Math.abs(diffY) > Math.abs(diffX)) {
                isScrolling = true;
                return;
              }

              // Only handle horizontal swipes
              if (!isScrolling) {
                e.preventDefault(); // Prevent page scroll during horizontal swipe
                this.slider.style.transform = `translateX(${diffX}px)`;
              }
            },
            { passive: false }
          );

          this.slider.addEventListener(
            'touchend',
            (e) => {
              if (this.isAnimating || isScrolling) return;

              this.touchEndX = e.changedTouches[0].screenX;
              this.handleSwipe();
              this.slider.style.transform = '';
            },
            { passive: true }
          );
        }
      }

      handleSwipe() {
        const swipeThreshold = 50; // Minimum distance for a swipe
        const swipeDistance = this.touchEndX - this.touchStartX;

        if (Math.abs(swipeDistance) < swipeThreshold) return;

        this.isAnimating = true;
        if (swipeDistance > 0) {
          // Swiped right - go to previous slide
          this.onButtonClick('prev');
        } else {
          // Swiped left - go to next slide
          this.onButtonClick('next');
        }

        // Reset animation flag after transition
        setTimeout(() => {
          this.isAnimating = false;
        }, 300);
      }

      onButtonClick(direction) {
        if (this.isAnimating) return;

        const newPage = direction === 'next' ? this.currentPage + 1 : this.currentPage - 1;
        if (newPage < 1 || newPage > this.pageCount) return;

        this.isAnimating = true;
        this.currentPage = newPage;
        this.updateSlider();

        // Reset animation flag after transition
        setTimeout(() => {
          this.isAnimating = false;
        }, 300);
      }

      updateSlider() {
        // Update active slide with smooth transition
        this.sliderItems.forEach((item, index) => {
          if (index + 1 === this.currentPage) {
            item.classList.add('is-active');
            item.style.opacity = '1';
          } else {
            item.classList.remove('is-active');
            item.style.opacity = '0';
          }
        });

        // Update counter
        const counter = this.querySelector('.slider-counter--current');
        if (counter) {
          counter.textContent = this.currentPage;
        }
      }
    }
  );
}
